import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getTDSNames,
    getTDSDCANames,
    getTDSSDCANames,
    saveTDSName,
    getTDSConfigDetails,
    saveTDSConfigDetails,
} from '../../api/AccountsAPI/tdsConfigureAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

/** TDS Name (Nature of Payment) dropdown */
export const fetchTDSNames = createAsyncThunk(
    'tdsConfigure/fetchTDSNames',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getTDSNames();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load TDS names');
        }
    }
);

/** DCA dropdown, loaded once up front */
export const fetchTDSDCANames = createAsyncThunk(
    'tdsConfigure/fetchTDSDCANames',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getTDSDCANames();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load DCA list');
        }
    }
);

/** SubDCA dropdown, scoped to the selected DCA */
export const fetchTDSSDCANames = createAsyncThunk(
    'tdsConfigure/fetchTDSSDCANames',
    async (dcaCode, { rejectWithValue }) => {
        try {
            const res = await getTDSSDCANames(dcaCode);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load SubDCA list');
        }
    }
);

/** Add a new TDS Name (Nature of Payment) master entry */
export const submitNewTDSName = createAsyncThunk(
    'tdsConfigure/submitNewTDSName',
    async ({ AddTdsName, AddTdscode }, { rejectWithValue }) => {
        try {
            const res = await saveTDSName({ AddTdsName, AddTdscode });
            const status = res.data?.Data;
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to add the TDS name');
            }
            return true;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to add the TDS name');
        }
    }
);

/** Grid: saved TDS configuration entries, optionally filtered by TDS Name */
export const fetchTDSConfigGrid = createAsyncThunk(
    'tdsConfigure/fetchTDSConfigGrid',
    async (code = '', { rejectWithValue }) => {
        try {
            // The Web API action requires the `code` query key to be present (even empty) —
            // an `undefined` value gets dropped by axios and the request 404s.
            const res = await getTDSConfigDetails(code ?? '');
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the TDS configuration grid');
        }
    }
);

/** Save a new TDS configuration entry */
export const submitTDSConfigDetails = createAsyncThunk(
    'tdsConfigure/submitTDSConfigDetails',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await saveTDSConfigDetails(payload);
            const status = res.data?.Data;
            if (status && status.toLowerCase() !== 'successfull' && status.toLowerCase() !== 'success') {
                return rejectWithValue(status);
            }
            return { tdsNames: payload.TdsNames };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save the TDS configuration');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    tdsNameList: [],
    dcaList: [],
    sdcaList: [],
    gridList: [],
    saveResult: null,
    addNameResult: null,

    loading: {
        tdsNames: false,
        dca: false,
        sdca: false,
        grid: false,
        save: false,
        addName: false,
    },
    errors: {
        tdsNames: null,
        dca: null,
        sdca: null,
        grid: null,
        save: null,
        addName: null,
    },
};

const tdsConfigureSlice = createSlice({
    name: 'tdsConfigure',
    initialState,
    reducers: {
        clearSDCAList: (state) => { state.sdcaList = []; },
        clearTDSSaveResult: (state) => { state.saveResult = null; state.errors.save = null; },
        clearAddNameResult: (state) => { state.addNameResult = null; state.errors.addName = null; },
        resetTDSConfigure: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTDSNames.pending, s => { s.loading.tdsNames = true; s.errors.tdsNames = null; })
            .addCase(fetchTDSNames.fulfilled, (s, a) => { s.loading.tdsNames = false; s.tdsNameList = a.payload; })
            .addCase(fetchTDSNames.rejected, (s, a) => { s.loading.tdsNames = false; s.errors.tdsNames = a.payload; });

        builder
            .addCase(fetchTDSDCANames.pending, s => { s.loading.dca = true; s.errors.dca = null; })
            .addCase(fetchTDSDCANames.fulfilled, (s, a) => { s.loading.dca = false; s.dcaList = a.payload; })
            .addCase(fetchTDSDCANames.rejected, (s, a) => { s.loading.dca = false; s.errors.dca = a.payload; });

        builder
            .addCase(fetchTDSSDCANames.pending, s => { s.loading.sdca = true; s.errors.sdca = null; })
            .addCase(fetchTDSSDCANames.fulfilled, (s, a) => { s.loading.sdca = false; s.sdcaList = a.payload; })
            .addCase(fetchTDSSDCANames.rejected, (s, a) => { s.loading.sdca = false; s.errors.sdca = a.payload; });

        builder
            .addCase(submitNewTDSName.pending, s => { s.loading.addName = true; s.errors.addName = null; s.addNameResult = null; })
            .addCase(submitNewTDSName.fulfilled, (s) => { s.loading.addName = false; s.addNameResult = true; })
            .addCase(submitNewTDSName.rejected, (s, a) => { s.loading.addName = false; s.errors.addName = a.payload; });

        builder
            .addCase(fetchTDSConfigGrid.pending, s => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchTDSConfigGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridList = a.payload; })
            .addCase(fetchTDSConfigGrid.rejected, (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; });

        builder
            .addCase(submitTDSConfigDetails.pending, s => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitTDSConfigDetails.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitTDSConfigDetails.rejected, (s, a) => { s.loading.save = false; s.errors.save = a.payload; });
    },
});

export const {
    clearSDCAList,
    clearTDSSaveResult,
    clearAddNameResult,
    resetTDSConfigure,
} = tdsConfigureSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectTDSNameList = s => s.tdsConfigure.tdsNameList;
export const selectTDSDCAList  = s => s.tdsConfigure.dcaList;
export const selectTDSSDCAList = s => s.tdsConfigure.sdcaList;
export const selectTDSGridList = s => s.tdsConfigure.gridList;
export const selectTDSSaveResult = s => s.tdsConfigure.saveResult;
export const selectTDSAddNameResult = s => s.tdsConfigure.addNameResult;

export const selectTDSNamesLoading = s => s.tdsConfigure.loading.tdsNames;
export const selectTDSDCALoading   = s => s.tdsConfigure.loading.dca;
export const selectTDSSDCALoading  = s => s.tdsConfigure.loading.sdca;
export const selectTDSGridLoading  = s => s.tdsConfigure.loading.grid;
export const selectTDSSaveLoading  = s => s.tdsConfigure.loading.save;
export const selectTDSAddNameLoading = s => s.tdsConfigure.loading.addName;
export const selectTDSGridError    = s => s.tdsConfigure.errors.grid;
export const selectTDSSaveError    = s => s.tdsConfigure.errors.save;
export const selectTDSAddNameError = s => s.tdsConfigure.errors.addName;

export default tdsConfigureSlice.reducer;
